import { useRef } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import BaseDatePicker from "../../../components/ui/BaseDatePicker";
import { fetchSubQualificationTypes } from "../../../services/api/qualification";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { addUserQualifications, updateUserQualifications } from "../../../services/api/user";
import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { validateUploadSize } from "../../../utils/helpers";

const getValidationSchema = (isEdit) =>
  yup.object({
    file: isEdit
      ? yup.mixed().nullable()
      : yup
          .mixed()
          .required("File is required")
          .test("fileSize", "File size is too large", (value, ctx) =>
            validateUploadSize(value, ctx),
          )
          .test(
            "fileType",
            "Unsupported file format (PDF, JPG, PNG, WEBP allowed)",
            (value) =>
              value &&
              ["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(value.type),
          ),
    qualificationSubTypeId: isEdit
      ? yup.number().nullable()
      : yup.number().required("Select license or certification"),
    startDate: isEdit ? yup.string().nullable() : yup.string().required("Select start date"),
    endDate: (isEdit ? yup.string().nullable() : yup.string().required("Select end date")).test(
      "after-start",
      "End date can't be before start date",
      function (value) {
        const { startDate } = this.parent;
        if (!value || !startDate) return true;
        // Compare calendar days only (equal dates are allowed).
        return !dayjs(value).startOf("day").isBefore(dayjs(startDate).startOf("day"));
      },
    ),
  });

const AddLicenseAndCertificationModal = ({
  open,
  close,
  qualificationSubTypeId,
  refetchUserQualificationsByType,
  certificationId,
  licencesId,
  isEdit,
  setSelectedUserCertificate,
  selectedUserCertificate,
  targetUserId,
}) => {
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  const {
    data: licencesData,
    isPending: isLicencesLoading,
    error: isLicencesError,
  } = useQuery({
    queryKey: ["licences", licencesId],
    queryFn: () => fetchSubQualificationTypes(licencesId),
    enabled: !!licencesId,
  });

  const {
    data: certificationsData,
    isPending: isCertificationsLoading,
    error: isCertificationsError,
  } = useQuery({
    queryKey: ["certifications", certificationId],
    queryFn: () => fetchSubQualificationTypes(certificationId),
    enabled: !!certificationId,
  });

  const {
    isPending: isUserQualificationsApiLoading,
    error: isUserQualificationsApiError,
    data: userQualificationData,
    mutateAsync: addUserQualificationsMutation,
  } = useMutation({
    mutationFn: (payload) => addUserQualifications(payload, targetUserId),
    enabled: false,
  });

  const {
    isPending: isUpdateUserQualificationsApiLoading,
    error: isUpdateUserQualificationsApiError,
    data: updatedUserQualificationData,
    mutateAsync: updateUserQualificationsMutation,
  } = useMutation({
    mutationFn: updateUserQualifications,
    enabled: false,
  });

  const formik = useFormik({
    initialValues: {
      file: null,
      qualificationSubTypeId:
        selectedUserCertificate?.qualificationSubTypeId || qualificationSubTypeId,
      startDate: selectedUserCertificate?.startDate,
      endDate: selectedUserCertificate?.endDate,
    },
    validationSchema: getValidationSchema(isEdit),
    onSubmit: (values) => {
      handleCertificationSubmit(values);
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const handleAddCertification = async (values) => {
    try {
      const formData = new FormData();
      formData.append("file", values?.file);

      Object.keys(values).forEach((key) => {
        if (key !== "file") {
          const value = key === "qualificationSubTypeId" ? Number(values[key]) : values[key]; // ✅ Convert to number
          formData.append(key, value);
        }
      });
      const certificationResponse = await addUserQualificationsMutation(formData);
      dispatch(
        showToast({
          message: "Certification added successfully",
          type: "success",
        }),
      );

      formik.resetForm();
      refetchUserQualificationsByType();
      formik.setFieldValue("file", null);

      close(false);
    } catch (error) {
      console.log(error);
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    }
  };

  const mergedQualifications = [
    ...(Array.isArray(licencesData?.data) ? licencesData.data : []),
    ...(Array.isArray(certificationsData?.data) ? certificationsData.data : []),
  ];
  const formattedQualifications = mergedQualifications?.map((qualification) => {
    return {
      label: qualification?.name,
      value: qualification?.id,
    };
  });

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target?.files[0];
    formik.setFieldValue("file", file);
  };

  // Remove the selected (not-yet-saved) file so the user can re-choose.
  const handleRemoveFile = () => {
    formik.setFieldValue("file", null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCertificationSubmit = (values) => {
    if (isEdit) {
      handleEditCertification();
    } else {
      handleAddCertification(values);
    }
  };

  const handleEditCertification = async () => {
    try {
      const { file, ...rest } = formik.values;

      const formData = new FormData();
      for (const key in rest) {
        if (rest[key] !== undefined && rest[key] !== null) {
          formData.append(key, rest[key]);
        }
      }

      if (file) {
        formData.append("file", file);
      }

      const certificationResponse = await updateUserQualificationsMutation({
        id: selectedUserCertificate.id,
        data: formData,
      });

      refetchUserQualificationsByType();
      dispatch(
        showToast({
          message: "Certificate updated successfully",
          type: "success",
        }),
      );
      formik.resetForm();
      close(false);
    } catch (error) {
      console.log(error);
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    }
  };

  return (
    <form>
      <BaseModal
        className="-top-[200px] overflow-auto"
        open={open}
        close={() => {
          close(false);
          formik.resetForm();
          setSelectedUserCertificate?.({
            qualificationSubTypeId: null,
            startDate: null,
            endDate: null,
          });
        }}
      >
        <div className="py-7 px-5 sm:px-12 w-full lg:w-[900px] xl:w-[1000px]">
          <h6 className="text-dark font-normal border-b pb-1">Select License or Certification</h6>
        </div>

        <div className="px-5 sm:px-12 grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-4 [&>*]:min-w-0">
          <div className="col-span-1 lg:col-span-2">
            <BaseAutoSelect
              disabled={!!qualificationSubTypeId}
              isDefaultField={false}
              placeholder="Search"
              options={formattedQualifications}
              id="qualificationSubTypeId"
              name="qualificationSubTypeId"
              value={formik.values.qualificationSubTypeId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.qualificationSubTypeId &&
                Boolean(formik.errors.qualificationSubTypeId)
              }
              helperText={
                formik.touched.qualificationSubTypeId && formik.errors.qualificationSubTypeId
              }
            />
          </div>

          <div>
            <p className="font-normal">Start Date</p>
            <BaseDatePicker
              className="border-none"
              type="date"
              id="startDate"
              name="startDate"
              value={formik.values.startDate ? dayjs(formik.values.startDate) : null}
              onChange={(event) => {
                const selectedDate = dayjs(event)
                  .utcOffset(0, true)
                  .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
                // Validate on setFieldValue (new value); mark touched WITHOUT
                // re-validating to avoid stale-value validation.
                formik.setFieldValue("startDate", selectedDate, true);
                formik.setFieldTouched("startDate", true, false);
                formik.setFieldTouched("endDate", true, false);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.startDate && Boolean(formik.errors.startDate)}
              helperText={formik.touched.startDate && formik.errors.startDate}
              slotProps={{
                textField: {
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      border: "none !important",
                      boxShadow: "1px 1px 4px 1px #00000021",
                      borderRadius: "5px",
                      backgroundColor: "white",
                      height: "48px",
                      display: "flex",
                      alignItems: "center",
                      margin: "8px 1.5px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none !important",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none !important",
                      boxShadow: "none",
                    },
                    "& .MuiInputBase-input": {
                      padding: "10px 14px",
                      fontSize: "13px",
                      height: "100% !important",
                      lineHeight: "1.5",
                    },

                    "& .MuiIconButton-root": {
                      backgroundColor: "white !important",
                      borderLeft: "none !important",
                    },
                  },
                },
              }}
            />
          </div>
          <div>
            <p className="font-normal">End Date</p>
            <BaseDatePicker
              className="border-none"
              type="date"
              id="endDate"
              name="endDate"
              minDate={formik.values.startDate ? dayjs(formik.values.startDate) : undefined}
              value={formik.values.endDate ? dayjs(formik.values.endDate) : null}
              onChange={(event) => {
                const selectedDate = dayjs(event)
                  .utcOffset(0, true)
                  .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
                // Validate on setFieldValue (new value); mark touched WITHOUT
                // re-validating, so a stale value doesn't keep the error visible.
                formik.setFieldValue("endDate", selectedDate, true);
                formik.setFieldTouched("endDate", true, false);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.endDate && Boolean(formik.errors.endDate)}
              helperText={formik.touched.endDate && formik.errors.endDate}
              slotProps={{
                textField: {
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      border: "none !important",
                      boxShadow: "1px 1px 4px 1px #00000021",
                      borderRadius: "5px",
                      backgroundColor: "white",
                      height: "48px",
                      display: "flex",
                      alignItems: "center",
                      margin: "8px 1.5px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none !important",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none !important",
                      boxShadow: "none",
                    },
                    "& .MuiInputBase-input": {
                      padding: "10px 14px",
                      fontSize: "13px",
                      height: "100% !important",
                      lineHeight: "1.5",
                    },

                    "& .MuiIconButton-root": {
                      backgroundColor: "white !important",
                      borderLeft: "none !important",
                    },
                  },
                },
              }}
            />
          </div>
          <div>
            <p className="font-normal mb-2">Upload JPG, PNG</p>
            <div className="w-full flex flex-wrap items-center gap-3">
              {/* Hidden File Input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: "none" }}
                id="file"
                name="file"
                accept=".pdf,.jpg,.png,.webp"
                onBlur={formik.handleBlur}
              />

              {/* File Display Section */}
              <div
                style={{ boxShadow: "0px 0px 6px 0px rgba(0, 0, 0, 0.10)" }}
                className="min-h-[40px] flex-1 min-w-[180px] px-3 py-[6.25px] rounded-md flex flex-wrap items-center"
              >
                <div className="min-w-0 max-w-full flex flex-wrap items-center">
                  {formik.values.file ? (
                    // Newly selected file: clickable to preview + removable.
                    <div className="my-[2px] inline-flex items-center gap-2 bg-[#E9E9EB] rounded-[4px] py-1 pl-4 pr-2 max-w-full">
                      <a
                        href={URL.createObjectURL(formik.values.file)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cursor-pointer text-blue-500 hover:underline font-300 truncate"
                      >
                        {formik.values.file.name}
                      </a>
                      <button
                        type="button"
                        onClick={handleRemoveFile}
                        className="flex items-center justify-center h-4 w-4 rounded-full text-gray-600 hover:bg-gray-300 hover:text-gray-800"
                        title="Remove file"
                      >
                        <i className="fa-solid fa-xmark text-xs"></i>
                      </button>
                    </div>
                  ) : selectedUserCertificate?.url || selectedUserCertificate?.digitalAsset?.url ? (
                    // Editing: show the already-uploaded file as a clickable chip.
                    <a
                      href={
                        selectedUserCertificate?.url || selectedUserCertificate?.digitalAsset?.url
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="my-[2px] inline-flex items-center gap-2 bg-[#E9E9EB] rounded-[4px] py-1 pl-4 pr-4 cursor-pointer text-blue-500 hover:underline font-300 truncate"
                    >
                      <i className="fa-solid fa-paperclip"></i>
                      {selectedUserCertificate?.fileName ||
                        selectedUserCertificate?.digitalAsset?.fileName ||
                        "View File"}
                    </a>
                  ) : (
                    <p className="text-gray-400 py-1">No file selected</p>
                  )}
                </div>
              </div>
              <BaseButton
                variant="outlined"
                color="primary"
                className="py-[7px] px-5 shrink-0 whitespace-nowrap"
                onClick={handleButtonClick}
              >
                Choose File
              </BaseButton>
            </div>
            {formik.errors.file && (
              <p className="text-red-600 text-xs mt-1.5">{formik.errors.file}</p>
            )}
          </div>
        </div>

        <div
          className="d-flex content-center bg-white py-6 mt-6"
          style={{
            boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
          }}
        >
          <BaseButton
            className=" px-[28px] py-[5px] bg-light text-primary hover:text-primary"
            variant="outlined"
            color="primary"
            style={{
              marginRight: "16px",
            }}
            onClick={() => {
              close(false);
              formik.resetForm();
              setSelectedUserCertificate?.({
                qualificationSubTypeId: null,
                startDate: null,
                endDate: null,
              });
            }}
          >
            Cancel
          </BaseButton>

          <BaseButton
            isLoading={isUserQualificationsApiLoading}
            loaderSize={24}
            loaderColor="primary"
            className="px-[20px] py-[5px]"
            color="primary"
            // Disabled by default; enabled only when there are valid unsaved changes.
            disabled={!formik.dirty || !formik.isValid || isUserQualificationsApiLoading}
            onClick={() => {
              formik.handleSubmit();
            }}
          >
            Save Changes
          </BaseButton>
        </div>
      </BaseModal>
    </form>
  );
};

export default AddLicenseAndCertificationModal;
